import dayjs from "@gtmhub/dayjs";
import { IField } from "@gtmhub/entities/models/models";
import { SupportedDataType, SupportedOperators } from "@gtmhub/insights";
import { Insight } from "@webapp/data-story/models/insights.models";
import { Parameter } from "@webapp/data-story/models/parameters-facade.models";
import { localize } from "@webapp/localization/utils/localization.utils";

export const getInsightUsedFilters = (insight: Insight, parameters: Parameter[]): Parameter[] => {
  return parameters.filter((parameter) => insight.usedFilters.includes(parameter.key));
};

export const isPeriodString = (value: unknown): boolean => {
  if (typeof value !== "string") {
    return false;
  }

  const dateStrings = value.split("|");
  if (dateStrings.length !== 2) {
    return false;
  }

  return !!Date.parse(dateStrings[0]) && !!Date.parse(dateStrings[1]);
};

export const convertPeriodStringToDateArray = (periodString: string): Date[] => {
  const [startDate, endDate] = periodString.split("|");

  return [dayjs(startDate).toDate(), dayjs(endDate).toDate()];
};

export const convertDateRangeObjectToString = ([startDate, endDate]: Date[]): string => `${dayjs(startDate).toISOString()}|${dayjs(endDate).toISOString()}`;

const supportedOperatorTranslationKeys: { [key in SupportedOperators]?: string } = {
  contains: "contains",
  doesNotContain: "does_not_contain",
  overlaps: "overlaps",
  doesNotOverlap: "does_not_overlap",
  equals: "equals_to",
  notEqual: "is_not_equal_to",
  hasLengthOf: "has_length_of",
  isContainedBy: "is_contained_by",
  isNotContainedBy: "is_not_contained_by",
  shorter: "is_shorter_than",
  longer: "is_longer_than",
};

const getInsightOperatorTranslationKey = (operator: SupportedOperators, dataType: SupportedDataType): string => {
  if (operator === SupportedOperators.Greater) {
    return dataType === SupportedDataType.Timestamp ||
      dataType === SupportedDataType.Date ||
      dataType === SupportedDataType.DateTime ||
      dataType === SupportedDataType.UnixTime
      ? "is_after"
      : "is_greater_than";
  } else if (operator === SupportedOperators.Less) {
    return dataType === SupportedDataType.Timestamp ||
      dataType === SupportedDataType.Date ||
      dataType === SupportedDataType.DateTime ||
      dataType === SupportedDataType.UnixTime
      ? "is_before"
      : "is_less_than";
  }

  return supportedOperatorTranslationKeys[operator] || "equals";
};

export const localizeInsightOperator = (operator: SupportedOperators, dataType: SupportedDataType): string =>
  localize(getInsightOperatorTranslationKey(operator, dataType));

export const compareFieldsByName = (a: IField, b: IField): boolean => a?.name === b?.name;
